<template>
  <!-- Rebuilt on the KarroFin components. Same endpoints, same upload flow,
       same response handling -- only the presentation moved. -->
  <div class="kf-page">
    <!-- Title-less: the navbar already says "Reports". The upload control is
         the page's one action, so it sits in the header row where every other
         KarroFin screen puts its primary button. The file input stays hidden
         and is still clicked through the ref. -->
    <PageHeader>
      <input
        id="pan_file_field"
        ref="report_upload"
        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        type="file"
        style="display: none"
        @change="uploadFile"
      />
      <v-btn
        color="primary"
        depressed
        class="text-none kf-cta"
        :loading="isUploading"
        :disabled="isUploading"
        @click="triggerFileUpload"
      >
        <LucideIcon name="upload" :size="18" class="mr-1" />
        {{ isUploading ? "Uploading..." : "Add report" }}
      </v-btn>
    </PageHeader>

    <FilterBar
      :search.sync="search_query"
      :show-date="false"
      search-placeholder="Search by report name or uploader"
      @change="getReportList()"
      @reset="resetFilters"
    >
      <div v-if="!filterLocked('reports', 'flag_imported')" class="kf-field">
        <span class="kf-field__label">Is imported</span>
        <v-select
          v-model="is_imported"
          :items="is_imported_list"
          item-text="text"
          item-value="value"
          placeholder="Any"
          dense
          outlined
          single-line
          clearable
          hide-details
          @change="getReportList()"
        ></v-select>
      </div>
    </FilterBar>

    <DataTable
      title="Reports"
      :headers="headers"
      :items="report_data"
      :loading="table_loading"
      :page="page_number"
      :total-pages="total_page_count"
      :total-count="total_count"
      empty-title="No reports match these filters"
      empty-message="Try clearing the search, or upload a new report."
      empty-icon="chart-column"
      @page-change="onPage"
    >
      <!-- Who uploaded, as recorded at upload time: the name reads first, so a
           shared login shows as that login ("Admin") rather than as whichever
           person's email happens to sit on it. Old rows have no saved name and
           fall back to the email. -->
      <template #[`item.uploaded_by`]="{ item }">
        <div class="kf-stack">
          <div class="kf-stack__main">
            {{ item.uploaded_by.name || item.uploaded_by.email || "-" }}
          </div>
          <div v-if="item.uploaded_by.name && item.uploaded_by.email" class="kf-stack__sub">
            {{ item.uploaded_by.email }}
          </div>
          <div v-if="item.uploaded_by.mobile" class="kf-stack__sub">
            {{ item.uploaded_by.mobile }}
          </div>
        </div>
      </template>

      <!-- Four states, each with the import's own message on hover. "Imported
           with problems" is amber: the file was read, but some rows reached no
           offer or contradict an earlier disbursal -- it used to show the same
           green tick as a clean import, so skipped disbursals went unnoticed. -->
      <template #[`item.flag_imported`]="{ item }">
        <div class="kf-import">
          <v-tooltip bottom max-width="420">
            <template #activator="{ on, attrs }">
              <span class="kf-import__state" v-bind="attrs" v-on="on">
                <v-icon :color="importState(item).color" small>{{
                  importState(item).icon
                }}</v-icon>
                <span :class="`kf-import__label--${importState(item).key}`">{{
                  importState(item).label
                }}</span>
              </span>
            </template>
            <span>{{ item.remark || "Reason not available" }}</span>
          </v-tooltip>
          <v-btn
            v-if="item.issue_count"
            icon
            small
            :loading="downloading === item.id"
            :title="`Download the ${item.issue_count} rows with problems`"
            @click.stop="downloadIssues(item)"
          >
            <LucideIcon name="download" :size="16" />
          </v-btn>
        </div>
      </template>

      <template #[`item.action`]="{ item }">
        <v-btn
          icon
          small
          class="mr-1"
          title="Open report"
          @click.stop="openReport(item)"
        >
          <LucideIcon name="eye" :size="18" />
        </v-btn>
        <v-btn icon small title="Delete report" @click.stop="deleteReport(item)">
          <LucideIcon name="trash-2" :size="18" />
        </v-btn>
      </template>
    </DataTable>
  </div>
</template>
<script>
import PageHeader from "@/components/karrofin/PageHeader.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import api from "@/app-js/karrofin/api";

/* import_status from the list API (reports/serializers.py). Reports imported
   before it existed have no status and fall back to flag_imported. */
const IMPORT_STATES = {
  in_progress: { label: "Importing", icon: "mdi-progress-clock", color: "grey" },
  failed: { label: "Failed", icon: "mdi-close-circle-outline", color: "error" },
  problems: { label: "With problems", icon: "mdi-alert-circle-outline", color: "warning" },
  success: { label: "Imported", icon: "mdi-check-circle-outline", color: "success" },
};

export default {
  components: {
    PageHeader,
    FilterBar,
    DataTable,
  },
  data() {
    return {
      is_imported: null,
      is_imported_list: [
        { text: "True", value: true },
        { text: "False", value: false },
      ],
      report_data: [],
      page_number: 1,
      total_page_count: 0,
      total_count: null,
      isUploading: false,
      /* New: the list had no loading flag at all, so the table used to sit
         empty while it fetched. The skeleton needs one. */
      table_loading: false,
      /* Owned by the page now rather than read from the shared navbar search. */
      search_query: "",
      /* Id of the report whose problem rows are downloading, for the spinner. */
      downloading: null,
      info: [],
      is_edit: false,
      headers: [
        { text: "Report ID", align: "start", value: "id", width: 110 },
        {
          text: "Report Name",
          align: "start",
          value: "report_name",
          width: 320,
        },
        { text: "Uploaded By", align: "start", value: "uploaded_by", width: 240 },
        { text: "Uploaded Date", align: "start", value: "created", width: 160 },
        {
          text: "Is Imported",
          align: "start",
          value: "flag_imported",
          width: 190,
        },
        { text: "Action", align: "center", value: "action", width: 120 },
      ],
    };
  },
  methods: {
    importState(item) {
      const key =
        item.import_status || (item.flag_imported ? "success" : "failed");
      return { key, ...(IMPORT_STATES[key] || IMPORT_STATES.success) };
    },
    downloadIssues(item) {
      this.downloading = item.id;
      api
        .exportReportIssues(item)
        .catch((err) =>
          api.downloadErrorMessage(err).then((message) => {
            this.showSnakeBar("error", message);
          })
        )
        .finally(() => {
          this.downloading = null;
        });
    },
    triggerFileUpload() {
      this.$refs.report_upload.click();
    },
    openReport(item) {
      /* The report is a spreadsheet, so it opens in the Office web viewer --
         which means `report_file` has to be a publicly reachable URL. */
      const viewer =
        "https://view.officeapps.live.com/op/embed.aspx?src=" +
        encodeURIComponent(item.report_file);
      window.open(viewer, "_blank");
    },
    uploadFile() {
      const fileInput = this.$refs.report_upload;
      const file = fileInput.files[0];

      /* Nothing picked: the button used to stay on "Uploading..." for good. */
      if (!file) {
        return;
      }

      this.isUploading = true;
      const formData = new FormData();
      formData.append("report_file", file);
      formData.append("report_name", file.name);

      const self = this;

      const successHandler = () => {
        self.getReportList();
      };

      /* The input is cleared whatever the outcome. Cleared on success only, a
         failed upload left the file selected, and picking the same file again
         fired no change event -- the retry silently did nothing. */
      const finallyHandler = () => {
        self.isUploading = false;
        fileInput.value = "";
      };
      self.request_POST(
        self,
        self.$urls.UPLOAD_REPORT,
        formData,
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
    getReportList() {
      const self = this;
      self.table_loading = true;
      const param = {
        page_number: this.page_number,
        search_query: this.search_query,
        flag_imported: this.is_imported,
      };
      const successHandler = (response) => {
        self.report_data = response.data.result.data;
        self.total_page_count = response.data.result.total_page_count;
        self.total_count =
          response.data.result.total_count !== undefined
            ? response.data.result.total_count
            : null;
      };

      const finallyHandler = () => {
        self.table_loading = false;
      };

      self.request_GET(
        self,
        self.$urls.GET_REPORTS_LIST,
        param,
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
    onPage(page) {
      this.page_number = page;
      this.getReportList();
    },
    resetFilters() {
      this.is_imported = null;
      this.search_query = "";
      this.getReportList();
    },
    deleteReport(item) {
      let lookup_key = item.id;
      const successHandler = () => {
        this.getReportList();
      };
      const finallyHandler = () => {};
      const self = this;
      self.request_POST(
        self,
        `${self.$urls.DELETE_REPORT}${lookup_key}/`,
        {},
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
  },
  mounted() {
    this.getReportList();
  },
};
</script>

<style scoped>
.kf-import {
  display: flex;
  align-items: center;
  gap: 4px;
}
.kf-import__state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: default;
  font-size: var(--kf-fs-body);
}
.kf-import__label--problems {
  color: var(--v-warning-darken1, #b26a00);
  font-weight: 500;
}
.kf-import__label--failed {
  color: var(--v-error-base, #c62828);
}
.kf-cta.v-btn {
  letter-spacing: 0;
  font-weight: 600;
}
/* Two values in one cell: the identifier reads first, its contact detail sits
   under it a tier quieter -- the same shape the KarroFin user cells use. */
.kf-stack__main {
  font-size: var(--kf-fs-body);
  color: #000;
}
.kf-stack__sub {
  font-size: var(--kf-fs-meta);
  color: var(--kf-text-muted);
  font-weight: 400;
}
</style>
